const de = {
  title: 'Gespräch vorbereiten',
  action: 'Gespräch vorbereiten',
  description: 'Kompakte, faktenbasierte Vorbereitung für {{customer}}.',
  loading: 'Relevante ERP-Fakten werden zusammengestellt …',
  error: 'Die Gesprächsvorbereitung konnte nicht geladen werden.',
  retry: 'Erneut versuchen',
  aiBadge: 'KI-gestützt sortiert',
  fallbackBadge: 'Ohne KI zusammengestellt',
  aiNotice:
    'Ollama hat ausschließlich vorhandene Fakten ausgewählt. Prioritäten und Sperren stammen weiterhin aus den ERP-Regeln.',
  fallbackNotice:
    'Ollama war nicht verfügbar oder lieferte keine gültige Antwort. Die Fakten wurden deterministisch zusammengestellt.',
  empty: 'Für diesen Kunden liegen noch keine relevanten Fakten vor.',
  safety: 'Vor dem Verkaufsgespräch klären',
  facts: {
    LAST_ORDER: 'Letzte Bestellung am {{date}} (vor {{daysAgo}} Tagen).',
    REORDER_OVERDUE:
      'Üblicher Bestellrhythmus {{rangeMin}}–{{rangeMax}} Tage; Erwartung um {{deviationDays}} Tage überschritten.',
    REORDER_EXPECTED: 'Nächste Bestellung ungefähr am {{expectedDate}} erwartet.',
    REORDER_INSUFFICIENT_DATA:
      'Für eine individuelle Prognose reichen {{orderCount}} Bestellungen noch nicht aus.',
    SEASONAL_PAUSE: 'Saisonkunde: aktuell wird keine Bestellung erwartet.',
    TYPICAL_PRODUCT_CATEGORY: '{{name}} gehört zu den typischen Warengruppen.',
    MISSING_REGULAR_PRODUCT: 'Regelmäßig bestellter Artikel fehlt: {{name}}.',
    MISSING_PRODUCT_CATEGORY: 'Typische Warengruppe fehlt: {{name}}.',
    OPEN_QUOTE: 'Angebot {{number}} vom {{date}} ist noch offen.',
    OPEN_TASK: 'Offener nächster Schritt: {{title}} (fällig {{dueAt}}).',
    RECENT_CONTACT: 'Letzter relevanter Kontakt: {{summary}}.',
    OPEN_COMPLAINT: 'Offene Reklamation: {{subject}} ({{severity}}).',
    CUSTOMER_RISK: 'Risikostatus des Kunden: {{status}}.',
    DELIVERY_BLOCK: 'Für den Kunden besteht eine Liefersperre.',
    SALES_BLOCK: 'Für den Kunden besteht eine Verkaufssperre.',
    OVERDUE_INVOICE: '{{count}} überfällige Rechnung(en) oder aktive Mahnvorgänge prüfen.',
  },
} as const;

const en = {
  title: 'Prepare call',
  action: 'Prepare call',
  description: 'A concise, fact-based briefing for {{customer}}.',
  loading: 'Collecting relevant ERP facts …',
  error: 'The call preparation could not be loaded.',
  retry: 'Try again',
  aiBadge: 'AI-assisted ordering',
  fallbackBadge: 'Prepared without AI',
  aiNotice:
    'Ollama selected existing facts only. Priorities and restrictions still come from deterministic ERP rules.',
  fallbackNotice:
    'Ollama was unavailable or returned an invalid response. The facts were assembled deterministically.',
  empty: 'No relevant facts are available for this customer yet.',
  safety: 'Resolve before a sales conversation',
  facts: {
    LAST_ORDER: 'The last order was placed on {{date}} ({{daysAgo}} days ago).',
    REORDER_OVERDUE:
      'Typical reorder cadence is {{rangeMin}}–{{rangeMax}} days; the expectation is {{deviationDays}} days overdue.',
    REORDER_EXPECTED: 'The next order is expected around {{expectedDate}}.',
    REORDER_INSUFFICIENT_DATA:
      '{{orderCount}} orders are not yet enough for an individual forecast.',
    SEASONAL_PAUSE: 'Seasonal customer: no order is currently expected.',
    TYPICAL_PRODUCT_CATEGORY: '{{name}} is one of the typical product categories.',
    MISSING_REGULAR_PRODUCT: 'A regularly ordered product is missing: {{name}}.',
    MISSING_PRODUCT_CATEGORY: 'A typical product category is missing: {{name}}.',
    OPEN_QUOTE: 'Quote {{number}} from {{date}} is still open.',
    OPEN_TASK: 'Open next step: {{title}} (due {{dueAt}}).',
    RECENT_CONTACT: 'Latest relevant contact: {{summary}}.',
    OPEN_COMPLAINT: 'Open complaint: {{subject}} ({{severity}}).',
    CUSTOMER_RISK: 'Customer risk status: {{status}}.',
    DELIVERY_BLOCK: 'The customer has a delivery block.',
    SALES_BLOCK: 'The customer has a sales block.',
    OVERDUE_INVOICE: 'Review {{count}} overdue invoice(s) or active dunning cases.',
  },
} as const;

export const salesPreparationDe = de;
export const salesPreparationEn = en;
export const salesPreparationTr = {
  ...en,
  title: 'Görüşmeye hazırlan',
  action: 'Görüşmeye hazırlan',
  description: '{{customer}} için kısa ve olgulara dayalı hazırlık.',
  loading: 'İlgili ERP olguları derleniyor …',
  error: 'Görüşme hazırlığı yüklenemedi.',
  retry: 'Yeniden dene',
  aiBadge: 'Yapay zekâ destekli sıralama',
  fallbackBadge: 'Yapay zekâ olmadan hazırlandı',
  aiNotice:
    'Ollama yalnızca mevcut olguları seçti; öncelikler ve engeller ERP kurallarından gelir.',
  fallbackNotice: 'Ollama kullanılamadı veya geçersiz yanıt verdi; olgular belirlenimsel derlendi.',
  empty: 'Bu müşteri için henüz ilgili olgu yok.',
  safety: 'Satış görüşmesinden önce çözün',
  facts: {
    ...en.facts,
    LAST_ORDER: 'Son sipariş {{date}} tarihinde verildi ({{daysAgo}} gün önce).',
    REORDER_OVERDUE:
      'Tipik sipariş aralığı {{rangeMin}}–{{rangeMax}} gün; beklenti {{deviationDays}} gün aşıldı.',
    REORDER_EXPECTED: 'Sonraki sipariş yaklaşık {{expectedDate}} tarihinde bekleniyor.',
    REORDER_INSUFFICIENT_DATA: '{{orderCount}} sipariş kişisel tahmin için henüz yeterli değil.',
    SEASONAL_PAUSE: 'Sezonluk müşteri: şu anda sipariş beklenmiyor.',
    TYPICAL_PRODUCT_CATEGORY: '{{name}}, tipik ürün gruplarından biridir.',
    MISSING_REGULAR_PRODUCT: 'Düzenli sipariş edilen ürün eksik: {{name}}.',
    MISSING_PRODUCT_CATEGORY: 'Tipik ürün grubu eksik: {{name}}.',
    OPEN_QUOTE: '{{date}} tarihli {{number}} teklifi hâlâ açık.',
    OPEN_TASK: 'Açık sonraki adım: {{title}} (son tarih {{dueAt}}).',
    RECENT_CONTACT: 'Son ilgili temas: {{summary}}.',
    OPEN_COMPLAINT: 'Açık şikâyet: {{subject}} ({{severity}}).',
    CUSTOMER_RISK: 'Müşteri risk durumu: {{status}}.',
    DELIVERY_BLOCK: 'Müşteride teslimat engeli var.',
    SALES_BLOCK: 'Müşteride satış engeli var.',
    OVERDUE_INVOICE: '{{count}} gecikmiş fatura veya ihtar sürecini inceleyin.',
  },
};
export const salesPreparationFr = {
  ...en,
  title: 'Préparer l’appel',
  action: 'Préparer l’appel',
  description: 'Une préparation concise et factuelle pour {{customer}}.',
  loading: 'Collecte des faits ERP pertinents …',
  error: 'La préparation de l’appel n’a pas pu être chargée.',
  retry: 'Réessayer',
  aiBadge: 'Tri assisté par IA',
  fallbackBadge: 'Préparé sans IA',
  aiNotice:
    'Ollama a uniquement sélectionné des faits existants; les priorités et blocages restent déterministes.',
  fallbackNotice:
    'Ollama était indisponible ou invalide; les faits ont été assemblés de façon déterministe.',
  empty: 'Aucun fait pertinent n’est encore disponible pour ce client.',
  safety: 'À résoudre avant l’entretien commercial',
  facts: {
    ...en.facts,
    LAST_ORDER: 'Dernière commande le {{date}} (il y a {{daysAgo}} jours).',
    REORDER_OVERDUE:
      'Rythme habituel de {{rangeMin}} à {{rangeMax}} jours; attente dépassée de {{deviationDays}} jours.',
    REORDER_EXPECTED: 'Prochaine commande attendue vers le {{expectedDate}}.',
    REORDER_INSUFFICIENT_DATA:
      '{{orderCount}} commandes ne suffisent pas encore à une prévision individuelle.',
    SEASONAL_PAUSE: 'Client saisonnier : aucune commande n’est attendue actuellement.',
    TYPICAL_PRODUCT_CATEGORY: '{{name}} fait partie des catégories habituelles.',
    MISSING_REGULAR_PRODUCT: 'Produit commandé régulièrement manquant : {{name}}.',
    MISSING_PRODUCT_CATEGORY: 'Catégorie habituelle manquante : {{name}}.',
    OPEN_QUOTE: 'Le devis {{number}} du {{date}} est encore ouvert.',
    OPEN_TASK: 'Prochaine étape ouverte : {{title}} (échéance {{dueAt}}).',
    RECENT_CONTACT: 'Dernier contact pertinent : {{summary}}.',
    OPEN_COMPLAINT: 'Réclamation ouverte : {{subject}} ({{severity}}).',
    CUSTOMER_RISK: 'Statut de risque du client : {{status}}.',
    DELIVERY_BLOCK: 'Le client fait l’objet d’un blocage de livraison.',
    SALES_BLOCK: 'Le client fait l’objet d’un blocage de vente.',
    OVERDUE_INVOICE: 'Vérifier {{count}} facture(s) échue(s) ou procédure(s) de relance.',
  },
};
export const salesPreparationEs = {
  ...en,
  title: 'Preparar llamada',
  action: 'Preparar llamada',
  description: 'Preparación breve y basada en hechos para {{customer}}.',
  loading: 'Recopilando hechos relevantes del ERP …',
  error: 'No se pudo cargar la preparación de la llamada.',
  retry: 'Reintentar',
  aiBadge: 'Orden asistido por IA',
  fallbackBadge: 'Preparado sin IA',
  aiNotice:
    'Ollama solo seleccionó hechos existentes; las prioridades y bloqueos siguen las reglas del ERP.',
  fallbackNotice:
    'Ollama no estaba disponible o respondió de forma inválida; los hechos se ordenaron de forma determinista.',
  empty: 'Aún no hay hechos relevantes para este cliente.',
  safety: 'Resolver antes de la conversación comercial',
  facts: {
    ...en.facts,
    LAST_ORDER: 'Último pedido el {{date}} (hace {{daysAgo}} días).',
    REORDER_OVERDUE:
      'Ritmo habitual de {{rangeMin}} a {{rangeMax}} días; previsión vencida por {{deviationDays}} días.',
    REORDER_EXPECTED: 'El próximo pedido se espera aproximadamente el {{expectedDate}}.',
    REORDER_INSUFFICIENT_DATA:
      '{{orderCount}} pedidos aún no bastan para una previsión individual.',
    SEASONAL_PAUSE: 'Cliente estacional: actualmente no se espera un pedido.',
    TYPICAL_PRODUCT_CATEGORY: '{{name}} es una de las categorías habituales.',
    MISSING_REGULAR_PRODUCT: 'Falta un producto pedido regularmente: {{name}}.',
    MISSING_PRODUCT_CATEGORY: 'Falta una categoría habitual: {{name}}.',
    OPEN_QUOTE: 'La oferta {{number}} del {{date}} sigue abierta.',
    OPEN_TASK: 'Siguiente paso abierto: {{title}} (vence {{dueAt}}).',
    RECENT_CONTACT: 'Último contacto relevante: {{summary}}.',
    OPEN_COMPLAINT: 'Reclamación abierta: {{subject}} ({{severity}}).',
    CUSTOMER_RISK: 'Estado de riesgo del cliente: {{status}}.',
    DELIVERY_BLOCK: 'El cliente tiene un bloqueo de entrega.',
    SALES_BLOCK: 'El cliente tiene un bloqueo de venta.',
    OVERDUE_INVOICE: 'Revisar {{count}} factura(s) vencida(s) o procesos de cobro.',
  },
};
export const salesPreparationRu = {
  ...en,
  title: 'Подготовить разговор',
  action: 'Подготовить разговор',
  description: 'Краткая подготовка по фактам для клиента {{customer}}.',
  loading: 'Собираются актуальные факты ERP …',
  error: 'Не удалось загрузить подготовку к разговору.',
  retry: 'Повторить',
  aiBadge: 'Сортировка с помощью ИИ',
  fallbackBadge: 'Подготовлено без ИИ',
  aiNotice:
    'Ollama выбрала только существующие факты; приоритеты и ограничения определяются правилами ERP.',
  fallbackNotice: 'Ollama недоступна или ответ некорректен; факты собраны детерминированно.',
  empty: 'Для этого клиента пока нет подходящих фактов.',
  safety: 'Уточнить до разговора о продаже',
  facts: {
    ...en.facts,
    LAST_ORDER: 'Последний заказ от {{date}} ({{daysAgo}} дн. назад).',
    REORDER_OVERDUE:
      'Обычный интервал {{rangeMin}}–{{rangeMax}} дн.; ожидание превышено на {{deviationDays}} дн.',
    REORDER_EXPECTED: 'Следующий заказ ожидается примерно {{expectedDate}}.',
    REORDER_INSUFFICIENT_DATA:
      '{{orderCount}} заказов пока недостаточно для индивидуального прогноза.',
    SEASONAL_PAUSE: 'Сезонный клиент: сейчас заказ не ожидается.',
    TYPICAL_PRODUCT_CATEGORY: '{{name}} относится к типичным товарным группам.',
    MISSING_REGULAR_PRODUCT: 'Отсутствует регулярно заказываемый товар: {{name}}.',
    MISSING_PRODUCT_CATEGORY: 'Отсутствует типичная товарная группа: {{name}}.',
    OPEN_QUOTE: 'Предложение {{number}} от {{date}} всё ещё открыто.',
    OPEN_TASK: 'Открытый следующий шаг: {{title}} (срок {{dueAt}}).',
    RECENT_CONTACT: 'Последний значимый контакт: {{summary}}.',
    OPEN_COMPLAINT: 'Открытая претензия: {{subject}} ({{severity}}).',
    CUSTOMER_RISK: 'Статус риска клиента: {{status}}.',
    DELIVERY_BLOCK: 'Для клиента действует блокировка поставок.',
    SALES_BLOCK: 'Для клиента действует блокировка продаж.',
    OVERDUE_INVOICE: 'Проверьте {{count}} просроченных счетов или процедур взыскания.',
  },
};
